import { useEffect, useMemo, useRef, useState } from 'react';
import type { Instance } from 'tippy.js';
import inputMasker from '../../helpers/inputMasker';
import { DropdownContent } from '../Dropdown/DropdownContent';
import { Tooltip } from '../Tooltip';
import codes from './codes';
import { PhoneInputWrapper } from './styles';

export default function PhoneInput({
    parentProps,
    phoneInputProps,
    countryInputProps,
    handleReturnCodeNumber,
}: {
    parentProps?: React.InputHTMLAttributes<HTMLDivElement>;
    phoneInputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    countryInputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    handleReturnCodeNumber?: (codeNumber: string) => void;
}) {
    const [countryNumber, setCountryNumber] = useState<string>('+55');
    const [isFilterEnabled, setFilterEnabled] = useState(false);
    const tooltipRef = useRef<Instance>();

    const dropdownElements = useMemo(() => {
        let rawResult = codes;

        if (isFilterEnabled) {
            rawResult = codes.filter(v =>
                v.dial_code.startsWith(countryNumber)
            );
        }

        return rawResult.map((d, index) => ({
            id: d.dial_code + index,
            value: d.dial_code,
        }));
    }, [isFilterEnabled, countryNumber]);

    useEffect(() => {
        handleReturnCodeNumber && handleReturnCodeNumber(countryNumber);
    }, [countryNumber, handleReturnCodeNumber]);

    return (
        <PhoneInputWrapper
            aria-invalid={parentProps && parentProps['aria-invalid']}
        >
            <Tooltip
                arrow={false}
                placement="bottom"
                trigger="click"
                interactive={true}
                hideOnClick={'toggle'}
                offset={[0, 2]}
                appendTo={document.body}
                onClickOutside={instance => instance.hide()}
                content={
                    <DropdownContent
                        elements={dropdownElements}
                        longestValue={0}
                        setCurrentSelected={val => {
                            tooltipRef.current?.hide();
                            setCountryNumber(val);
                        }}
                        title={countryNumber}
                    />
                }
                onMount={instance => (tooltipRef.current = instance)}
            >
                <input
                    className="number-only"
                    type="text"
                    value={countryNumber}
                    maxLength={inputMasker.getMaxLength('COUNTRY_CODE_NUMBER')}
                    {...countryInputProps}
                    onChange={inputMasker.handleChange(
                        'COUNTRY_CODE_NUMBER',
                        e => {
                            setFilterEnabled(true);
                            setCountryNumber(e.target.value);
                            if (countryInputProps?.onChange) {
                                countryInputProps.onChange(e);
                            }
                        }
                    )}
                    autoComplete={'off'}
                    onBlur={e => {
                        setFilterEnabled(false);
                        countryInputProps?.onBlur &&
                            countryInputProps?.onBlur(e);
                    }}
                ></input>
            </Tooltip>
            <span className="divider" />
            <input
                type={'text'}
                maxLength={inputMasker.getMaxLength('PHONE')}
                {...phoneInputProps}
            />
        </PhoneInputWrapper>
    );
}
